import {
	Form,
	Row,
	Col,
	Input,
	DatePicker,
	Select,
	Button,
	Modal,
	InputNumber,
	Checkbox,
} from 'antd';
import styles from './Pricing.module.less';
import FormItem from 'components/form/FormItem';
import api, {
	CreateHomescreenEventCommand,
	DownloadableContentDetailDto,
	ProductListItemDto,
	UpdateHomescreenEventCommand,
} from 'api';
import ImageFileUpload from 'components/ImageFileUpload';
import moment from 'moment-timezone';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { FileYupSchema, GetUploadLink, ImageFileModel } from 'utils/fileUtils';
import cx from 'classnames';
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, useLocation } from 'react-router-dom';
import OmniNotification from 'components/OmniNotification';
import { isEmpty } from 'lodash';
import {
	DLCTabName,
	IDownloadableContentDetailDto,
} from './EditDownloadableContent';

const MAX_PRICE = 99999.99;

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		price: yup
			.number()
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('downloadableContentEdit.tabs.pricing.form.price.title'),
				}),
			)
			.min(0, t('addProductPage.ProductPrice.validation'))
			.max(
				MAX_PRICE,
				t('validation.maxNumber', {
					fieldName: t('downloadableContentEdit.tabs.pricing.form.price.title'),
					count: MAX_PRICE,
				}),
			),
		isConfirmedWithPrice: yup.boolean().test(
			'isConfrimed',
			({ value }) =>
				t('downloadableContentEdit.tabs.pricing.form.checkbox.error'),
			(value) => {
				return value == true;
			},
		),
	});

const { Option } = Select;

type Props = {
	id: number;
	data: IDownloadableContentDetailDto;
	onSave: (number: string) => void;
};

interface FormValues {
	price: number;
	isConfirmedWithPrice: boolean;
}

export interface PricingRef {
	validatePricingTab: () => void;
}

export default forwardRef(function Pricing(props: Props, ref: any) {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const dispatch: AppDispatch = useDispatch();
	const { id, data, onSave } = props;
	const history = useHistory();
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const [submitButtonEnabled, SetSubmitButtonEnabled] = useState(true);
	const methods = useForm<FormValues>({
		mode: 'all',
		defaultValues: {},
		resolver: yupResolver(validationSchema),
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [timesOfLoadData, setTimesOfLoadData] = useState<number>(0);

	const {
		control,
		handleSubmit,
		setError,
		reset,
		formState: { isDirty },
		setValue,
		trigger,
	} = methods;

	useImperativeHandle(ref, () => ({
		async validatePricingTab() {
			return await trigger();
		},
	}));

	const submitEvent = (values) => {
		handleSubmit(async (values: FormValues) => {
			setIsSubmitting(true);
			api
				.updatePricingForDLC({
					id: id,
					price: values.price,
					confirmation: values.isConfirmedWithPrice,
				})
				.then(() => {
					OmniNotification.success({
						message: t('downloadableContentEdit.message.update.success.title'),
						description: t(
							'downloadableContentEdit.message.update.success.description',
						),
					});
					onSave(DLCTabName.Pricing);
				})
				.catch(() => {
					OmniNotification.error({
						message: t('notification.error.title'),
						description: t('api.defaultError'),
					});
					setIsSubmitting(false);
				});
		})();
	};

	useEffect(() => {
		if (isSubmitting || timesOfLoadData == 0) {
			reset({
				price: data.price,
				isConfirmedWithPrice: data.pricingConfirmation,
			});
		}

		if (
			!isEmpty(data) &&
			(!data.tabName || data.tabName == DLCTabName.Pricing)
		) {
			setTimesOfLoadData(timesOfLoadData + 1);
		}
	}, [data]);

	useEffect(() => {
		if (timesOfLoadData > 1) {
			setIsSubmitting(false);
		}
	}, [timesOfLoadData]);

	return (
		<>
			<FormProvider {...methods}>
				<Form data-testid="form" layout="vertical" onFinish={submitEvent}>
					<Row>
						<div style={{ flexDirection: 'row', alignItems: 'center' }}>
							<span className={styles.title}>
								{t('downloadableContentEdit.tabs.pricing.title')}
							</span>
							<span className={styles.description}>
								{t('downloadableContentEdit.tabs.pricing.description')}
							</span>
						</div>
					</Row>
					<Col
						style={{
							marginTop: 16,
						}}
					>
						<FormItem
							name="price"
							label={t('downloadableContentEdit.tabs.pricing.form.price.title')}
							control={control}
							className={styles.numberField}
							render={({ field }) => (
								<InputNumber
									{...field}
									addonBefore="$"
									controls={false}
									precision={2}
									size="large"
									data-testid="price"
								/>
							)}
						/>
					</Col>
					<Row>
						<Col span={10}>
							<FormItem
								name="isConfirmedWithPrice"
								control={control}
								render={({ field }) => (
									<Checkbox
										data-testid="is-confirmed-with-price"
										checked={field.value}
										{...field}
										disabled={timesOfLoadData > 1 && (!isDirty || isSubmitting)}
									>
										{t(
											'downloadableContentEdit.tabs.pricing.form.checkbox.description',
										)}
									</Checkbox>
								)}
							/>
						</Col>
					</Row>
					<Button
						data-testid="pricing-tab-save-btn"
						loading={isSubmitting}
						type="primary"
						htmlType="submit"
						disabled={timesOfLoadData > 1 && (!isDirty || isSubmitting)}
						size="large"
					>
						{t('common.Save')}
					</Button>
				</Form>
			</FormProvider>
		</>
	);
});
